import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Save, 
  X,
  MapPin, 
  Upload,
  AlertTriangle
} from 'lucide-react';
import { Strayyo } from '../../types';
import { strayyoService } from '../../services/admin';

interface StrayyoEditViewProps {
  strayyo: Strayyo;
  onBack: () => void;
  onSave: (updated: Strayyo) => void;
}

const StrayyoEditView: React.FC<StrayyoEditViewProps> = ({ 
  strayyo, 
  onBack, 
  onSave 
}) => {
  const [formData, setFormData] = useState({
    name: strayyo.name || '',
    aliases: strayyo.aliases?.join(', ') || '',
    type: strayyo.type,
    area: strayyo.area || '',
    latitude: strayyo.latitude || '',
    longitude: strayyo.longitude || '',
    healthStatus: strayyo.healthStatus,
    vaccinated: strayyo.vaccinated,
    sterilized: strayyo.sterilized,
    lastFed: strayyo.lastFed ? new Date(strayyo.lastFed).toISOString().slice(0, 16) : '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare update data
      const updateData: any = {
        name: formData.name.trim() || null,
        aliases: formData.aliases ? formData.aliases.split(',').map(a => a.trim()).filter(a => a) : [],
        type: formData.type,
        area: formData.area.trim() || null,
        healthStatus: formData.healthStatus,
        vaccinated: formData.vaccinated,
        sterilized: formData.sterilized,
      };

      // Handle coordinates
      if (formData.latitude && formData.longitude) {
        updateData.latitude = parseFloat(formData.latitude.toString());
        updateData.longitude = parseFloat(formData.longitude.toString());
      }

      // Handle lastFed
      if (formData.lastFed) {
        updateData.lastFed = new Date(formData.lastFed).toISOString();
      }

      const updated = await strayyoService.update(strayyo.id, updateData);
      setSuccess('Strayyo updated successfully!');
      setTimeout(() => {
        onSave(updated);
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update strayyo');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString()
          }));
        },
        (error) => {
          setError('Failed to get current location');
        }
      );
    } else {
      setError('Geolocation is not supported by this browser');
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Edit Strayyo
            </h1>
            <p className="text-sm text-gray-500">UID: {strayyo.uid}</p>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">Basic Information</h3>
            
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="mt-1 focus:ring-orange-500 focus:border-orange-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  placeholder="Optional name for the strayyo"
                />
              </div>

              <div>
                <label htmlFor="aliases" className="block text-sm font-medium text-gray-700">
                  Aliases (comma-separated)
                </label>
                <input
                  type="text"
                  name="aliases"
                  id="aliases"
                  value={formData.aliases}
                  onChange={handleInputChange}
                  className="mt-1 focus:ring-orange-500 focus:border-orange-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  placeholder="e.g., Tommy, Tom Tom, Big T"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Other names this Strayyo is known by
                </p>
              </div>

              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                  Type
                </label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm rounded-md"
                >
                  <option value="DOG">Dog</option>
                  <option value="CAT">Cat</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="area" className="block text-sm font-medium text-gray-700">
                  Area/Location
                </label>
                <input
                  type="text"
                  name="area"
                  id="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  className="mt-1 focus:ring-orange-500 focus:border-orange-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  placeholder="e.g., Central Park, Mumbai"
                />
              </div>

              <div>
                <label htmlFor="healthStatus" className="block text-sm font-medium text-gray-700">
                  Health Status
                </label>
                <select
                  id="healthStatus"
                  name="healthStatus"
                  value={formData.healthStatus}
                  onChange={handleInputChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm rounded-md"
                >
                  <option value="GOOD">Good</option>
                  <option value="FAIR">Fair</option>
                  <option value="POOR">Poor</option>
                  <option value="UNKNOWN">Unknown</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">Location Coordinates</h3>
            
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label htmlFor="latitude" className="block text-sm font-medium text-gray-700">
                  Latitude
                </label>
                <input
                  type="number"
                  name="latitude"
                  id="latitude"
                  step="any"
                  value={formData.latitude}
                  onChange={handleInputChange}
                  className="mt-1 focus:ring-orange-500 focus:border-orange-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  placeholder="e.g., 19.0760"
                />
              </div>

              <div>
                <label htmlFor="longitude" className="block text-sm font-medium text-gray-700">
                  Longitude
                </label>
                <input
                  type="number"
                  name="longitude"
                  id="longitude"
                  step="any"
                  value={formData.longitude}
                  onChange={handleInputChange}
                  className="mt-1 focus:ring-orange-500 focus:border-orange-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  placeholder="e.g., 72.8777"
                />
              </div>
            </div>

            <div className="mt-4">
              <button
                type="button"
                onClick={getCurrentLocation}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                <MapPin className="h-4 w-4 mr-2" />
                Use Current Location
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">Medical Information</h3>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="vaccinated"
                    name="vaccinated"
                    type="checkbox"
                    checked={formData.vaccinated}
                    onChange={handleInputChange}
                    className="focus:ring-orange-500 h-4 w-4 text-orange-600 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="vaccinated" className="font-medium text-gray-700">
                    Vaccinated
                  </label>
                  <p className="text-gray-500">Check if this strayyo has been vaccinated</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="sterilized"
                    name="sterilized"
                    type="checkbox"
                    checked={formData.sterilized}
                    onChange={handleInputChange}
                    className="focus:ring-orange-500 h-4 w-4 text-orange-600 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="sterilized" className="font-medium text-gray-700">
                    Sterilized
                  </label>
                  <p className="text-gray-500">Check if this strayyo has been sterilized</p>
                </div>
              </div>

              <div>
                <label htmlFor="lastFed" className="block text-sm font-medium text-gray-700">
                  Last Fed
                </label>
                <input
                  type="datetime-local"
                  name="lastFed"
                  id="lastFed"
                  value={formData.lastFed}
                  onChange={handleInputChange}
                  className="mt-1 focus:ring-orange-500 focus:border-orange-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
                <p className="mt-2 text-sm text-gray-500">
                  When was this strayyo last fed?
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onBack}
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StrayyoEditView;